import {
  aggregateVolumeByMuscle,
  catalogBySlug,
  dropsetSetTotals,
  toCanonicalKg,
  type CatalogMuscleSlug,
  type Exercise,
  type VolumeBySetInput,
} from '@jackedlogs/shared';
import { parseLocaleNumber } from './parseLocaleNumber';
import type { DraftEntry, LoggerDraft } from './state/loggerDraft';
import {
  bodyweightPerRepLoad,
  canonicalLoad,
  rawRoundsForSet,
  toDropsetRound,
} from './dropsetRoundMap';

// Pure active-workout rollup math for the Logger header/footer summaries.
// Extracted out of Totals.tsx (Track R slice C) so the computation lives
// apart from the React presentation. Mobile-local: the web Logger has no
// live-totals surface, so this stays here rather than in packages/shared
// until a second consumer exists.
//
// Mirrors the shared `setVolumeKg` semantic (sessionResult.ts):
//   weighted   → reps × weight × loadedSides
//   bodyweight → reps × (bw × pct/100 + extra)
// where pct comes from the exercise's `bodyweightLoadPct` (default
// 100 when absent) and `bw` is the user's Profile.bodyweightKg. Set
// keeps the SL-style "EXTRA field doubles as `weight`" convention:
// the DraftSet.weight value is repurposed as the bw extra, with
// extraNegative flipping the sign for band/machine-assistance.
// Pending sets are skipped (plans, not performed). A failed set counts like
// any completed set (AP-0p model): `reps` is the clean count and `fail` is a
// pure label — no exclusion.
export type EntryTotals = {
  sets: number;
  reps: number;
  volumeKg: number;
  /** Sum of the WEIGHT column across completed sets — the "total load
   *  moved" per set, reps-agnostic, uniform across both entry kinds
   *  (D1 2026-05-29):
   *    • weighted entry → Σ of each set's top-round load (always ≥ 0)
   *    • bodyweight entry → Σ of each set's per-rep load = bw×pct/100 +
   *      signed extra (band-assist subtracts). bw-INCLUSIVE, so the column
   *      reads the real load instead of 0/`—`. When bodyweightKg is unset
   *      (0) it collapses back to Σ signed extra.
   *  0 only when nothing was logged (no completed set, or pure-bw with
   *  bodyweightKg unset and no extra). */
  weightSumKg: number;
};

export const entryTotals = (
  entry: DraftEntry,
  exercise: Exercise | undefined,
  bodyweightKg: number,
): EntryTotals => {
  let sets = 0;
  let reps = 0;
  let volumeKg = 0;
  // Running sum of the weight/extra column (signed for bodyweight assist).
  let weightSumKg = 0;
  const pct =
    entry.isBodyweight && exercise?.bodyweightLoadPct !== undefined
      ? exercise.bodyweightLoadPct
      : 100;
  for (const s of entry.sets) {
    if (s.completedAt === null) continue;
    // A fail counts like any completed set (AP-0p model): `reps` is the clean
    // count (the failed rep was decremented at log time) and `fail` is a pure
    // label — no exclusion, no special-casing.
    sets += 1;
    // Every set (plain or dropset/myorep) is one SUBTOTAL: the shared
    // helper sums the top round + chain steps exactly once, so the footer
    // never double-counts a chain. A dropset counts as a single set (one
    // parent pill), matching the in-workout completion model.
    const rawRounds = rawRoundsForSet(s);
    const rounds = rawRounds.map((raw) =>
      toDropsetRound(raw, entry.isBodyweight, s.loadedSides, canonicalLoad(s.unit)),
    );
    const variant = s.dropset?.variant ?? 'classical';
    const totals = dropsetSetTotals(variant, rounds, {
      bodyweightKg,
      bodyweightLoadPct: pct,
    });
    reps += totals.reps;
    volumeKg += totals.volumeKg;
    // WEIGHT column total (D1 2026-05-29 — "uniformare con la sbarra").
    //   weighted   → Σ of the TOP round's load across sets (a dropset's
    //                "weight" is its top set; summing drops is meaningless).
    //   bodyweight → Σ of the per-set "load moved per rep" = bw×pct/100 +
    //                signed extra. bw-INCLUSIVE so the column reads the real
    //                load (matching the volume model) instead of 0/`—`, and
    //                so a band-assist subtracts. Reps live in volume, not
    //                here — exactly like the weighted column.
    if (entry.isBodyweight) {
      weightSumKg += bodyweightPerRepLoad(s, bodyweightKg, pct, canonicalLoad(s.unit));
    } else {
      const w = parseLocaleNumber(rawRounds[0]?.weight ?? '');
      if (Number.isFinite(w) && w !== 0) {
        weightSumKg += toCanonicalKg(Math.abs(w), s.unit);
      }
    }
  }
  return {
    sets,
    reps,
    volumeKg,
    weightSumKg,
  };
};

export type WorkoutTotals = EntryTotals & {
  // Catalog-vocab slug + rolled-up volume in canonical kg. Slice #4:
  // shared aggregateVolumeByMuscle reads Exercise.muscleContrib, so
  // multi-primary lifts distribute correctly across all trained
  // muscles instead of falling on the first one only.
  byMuscle: ReadonlyArray<{ muscle: CatalogMuscleSlug; volumeKg: number }>;
};

export const workoutTotals = (
  draft: LoggerDraft,
  catalog: ReadonlyArray<Exercise> | null,
  bodyweightKg: number,
): WorkoutTotals => {
  let sets = 0;
  let reps = 0;
  let volumeKg = 0;
  // Workout-level aggregates inherit EntryTotals shape so the type
  // contract holds; the per-entry "max" and "avg" fields don't have a
  // meaningful workout-wide aggregate (you can't average RPE across
  // distinct exercises) so we just zero them out at the workout level.
  // The WorkoutTotalsCard never reads them — only the per-entry footer
  // does.
  const bySlug = catalogBySlug(catalog ?? []);
  const perExerciseVolume: VolumeBySetInput[] = [];
  for (const entry of draft.entries) {
    const ex = bySlug.get(entry.exerciseId);
    const totals = entryTotals(entry, ex, bodyweightKg);
    sets += totals.sets;
    reps += totals.reps;
    volumeKg += totals.volumeKg;
    if (totals.volumeKg <= 0) continue;
    perExerciseVolume.push({
      exerciseId: entry.exerciseId,
      volumeKg: totals.volumeKg,
    });
  }
  const volumeByMuscle = aggregateVolumeByMuscle(perExerciseVolume, bySlug);
  const byMuscle = (
    Object.entries(volumeByMuscle) as Array<[CatalogMuscleSlug, number]>
  )
    .filter(([, v]) => v > 0)
    .map(([muscle, v]) => ({ muscle, volumeKg: v }))
    .sort((a, b) => b.volumeKg - a.volumeKg);
  return {
    sets,
    reps,
    volumeKg,
    weightSumKg: 0,
    byMuscle,
  };
};
